///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import { connect } from "react-redux";
import { flushSync } from "react-dom";
import { findIndex, cloneDeep } from "lodash";

import BidInformation from "../components/new-bid/BidInformation";
import BidStrategy from "../components/new-bid/BidStrategy";
import ModeInformation from "../components/new-bid/ModeInformation";
import ReviewBid from "../components/new-bid/ReviewBid";
import ProgressBar from "../components/new-bid/ProgressBar";
import Modal from "../components/Modal";
import NavigationBlocker from "../components/NavigationBlocker";
import getNewBidDefaults, {
  tlModeDefaults,
  ltlModeDefaults,
  imdlModeDefaults
} from "../helpers/getNewBidDefaults";
import {
  tlValidationDefaults,
  ltlValidationDefaults,
  imdlValidationDefaults
} from "../helpers/newBidValidationDefaults";
import { bidScacType, bidTypes, mode, userRole } from "../helpers/types";
import {
  updateStep,
  updateValidation,
  updateUnsavedChanges,
  resetState
} from "../slices/newBidSlice";
import {
  handleNewBidValidation,
  removeNewBidValidation,
  handleShipperValidation,
  handleStrategicQuantileValidation
} from "../helpers/handleNewBidValidation";
import mapBidDetails from "../helpers/mapBidDetails";
import { postAttachment, postAttachmentV2 } from "../api/attachments";
import { postNewBid } from "../api/bids";
import Spinner from "../components/icons/Spinner";
import { toggleNewBidFileUploadError } from "../slices/bidSummarySlice";
import messages from "../helpers/messages";
import ConfirmCancellationModal from "../components/Bid/details/ConfirmCancellationModal";

const defaultState = () => ({
  modalOpen: false,
  bid: { ...getNewBidDefaults },
  sendButtonClicked: false,
  priceButtonClicked: false,
  bidCreationError: false,
  showConfirmNavigation: true
});

class NewBid extends Component {
  constructor(props) {
    super(props);
    this.state = defaultState();
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.beforeunload);
    if (this.props.user.user.role === userRole.assetBasedPricingUser) {
      this.setBidState(
        "bidType",
        bidTypes.find(x => x.label === messages.coyoteFleet.defaultMessage)
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.beforeunload);
    this.props.dispatch(resetState());
  }

  beforeunload = event => {
    if (this.props.unsavedChanges) {
      // Cancel the event as stated by the standard.
      event.preventDefault();
      // Chrome requires returnValue to be set.
      event.returnValue = "";
    }
  };

  handleBackButton = () => {
    const { step, dispatch } = this.props;
    dispatch(updateStep(step - 1));
    window.scrollTo(0, 0);
  };

  handleNextButton = () => {
    const { step, validationData, dispatch, user, featureFlags } = this.props;
    const stepValidation = handleNewBidValidation(
      step,
      this.state.bid,
      validationData,
      user.user.division,
      featureFlags.crmEnableImdlUi
    );

    dispatch(updateValidation(stepValidation.validationData));
    if (
      stepValidation.allValid &&
      !this.state.bid.mainSalesRepDetails.loading
    ) {
      dispatch(updateStep(step + 1));
      window.scrollTo(0, 0);
    }
  };

  redirectToBid = (bidId, dispatch, history) => {
    window.scroll(0, 0);
    dispatch(resetState());
    flushSync(() => {
      this.setState({ showConfirmNavigation: false });
    });
    history.push(`/bid/${bidId}/summary`);
  };

  createBid = isPricedBySales => {
    const { files } = this.state.bid;
    const {
      validationData,
      dispatch,
      history,
      user,
      featureFlags: { useAttachmentV2Endpoint, crmEnableImdlUi }
    } = this.props;
    const stepValidation = handleNewBidValidation(
      4,
      this.state.bid,
      validationData,
      user.user.division,
      crmEnableImdlUi
    );

    if (
      stepValidation.allValid &&
      !this.state.bid.mainSalesRepDetails.loading
    ) {
      this.setState({
        sendButtonClicked: !isPricedBySales,
        priceButtonClicked: isPricedBySales,
        bidCreationError: false
      });

        postNewBid(mapBidDetails(this.state.bid, isPricedBySales, crmEnableImdlUi))
        .then(({ data: bidId }) => {
          if (files && files.length) {
            let attachmentAPI = useAttachmentV2Endpoint
              ? postAttachmentV2
              : postAttachment;
            attachmentAPI(
              bidId,
              true,
              files.map(f => f.file)
            )
              .then(() => {
                this.redirectToBid(bidId, dispatch, history);
              })
              .catch(() => {
                dispatch(toggleNewBidFileUploadError(true));
                this.redirectToBid(bidId, dispatch, history);
              });
          } else {
            this.redirectToBid(bidId, dispatch, history);
          }
        })
        .catch(() => {
          this.setState({ bidCreationError: true });
        });
    } else {
      dispatch(updateValidation(stepValidation.validationData));
    }
  };

  isModeCompatibleWithBidScac = (modeType, bidScac) => {
    if (this.props.featureFlags?.disableIMDLBid && modeType === mode.imdl) {
      return false;
    }

    if (!bidScac) {
      return true;
    }

    if (bidScac.value === bidScacType[1].value) {
      return modeType === mode.tl;
    }

    if (bidScac.value === bidScacType[0].value) {
      return modeType === mode.ltl || modeType === mode.imdl;
    }

    return true;
  };

  setBidState = (property, value) => {
    const { dispatch } = this.props;
    let stateObj = this.state.bid;
    stateObj[property] = value;

    if (property === "bidScac") {
      const updatedModes = stateObj.modes.filter(modeDetails =>
        this.isModeCompatibleWithBidScac(modeDetails.mode, value)
      );

      const removedModes = stateObj.modes.filter(
        modeDetails => !updatedModes.includes(modeDetails)
      );
      removedModes.forEach(modeDetails => {
        this.clearSectionValidation(modeDetails.mode);
      });

      if (updatedModes.length === 0) {
        stateObj.modes =
          value?.value === bidScacType[1].value
            ? [{ ...tlModeDefaults }]
            : [{ ...ltlModeDefaults }];
      } else {
        stateObj.modes = updatedModes;
      }
    }

    this.setState({ bid: { ...stateObj } });
    dispatch(updateUnsavedChanges(true));
  };

  removeError = (field, childField) => {
    let { step, validationData, dispatch } = this.props;
    const stepValidation = removeNewBidValidation(
      step,
      validationData,
      field,
      childField
    );

    dispatch(updateValidation(stepValidation.validationData));
  };

  handleModeSelection = type => {
    const {
      bid: { modes, bidScac }
    } = this.state;

    // Guard: prevent selecting incompatible modes
    if (!this.isModeCompatibleWithBidScac(type, bidScac)) {
      return;
    }

    const hasMode = modes.some(mode => mode.mode === type);
    let updatedModes;
    if (hasMode) {
      updatedModes = modes.filter(mode => mode.mode !== type);
    } else {
      if (type === mode.tl) {
        updatedModes = [...modes, { ...tlModeDefaults }];
      } else if (type === mode.ltl) {
        updatedModes = [...modes, { ...ltlModeDefaults }];
      } else {
        updatedModes = [...modes, { ...imdlModeDefaults }];
      }
    }

    this.clearSectionValidation(type);
    this.setBidState("modes", updatedModes);
  };

  clearSectionValidation = modeType => {
    const validationData = { ...this.props.validationData };
    switch (modeType) {
      case mode.tl:
        validationData.modeInformation = {
          ...validationData.modeInformation,
          tl: { ...tlValidationDefaults }
        };
        break;
      case mode.ltl:
        validationData.modeInformation = {
          ...validationData.modeInformation,
          ltl: { ...ltlValidationDefaults }
        };
        break;
      default:
        validationData.modeInformation = {
          ...validationData.modeInformation,
          imdl: { ...imdlValidationDefaults }
        };
        break;
    }
    this.props.dispatch(updateValidation(validationData));
  };

  setModeDetails = (mode, field, value) => {
    const updatedModes = [...this.state.bid.modes];
    const modeIndex = findIndex(updatedModes, { mode });

    updatedModes[modeIndex] = {
      ...updatedModes[modeIndex],
      [field]: value
    };
    this.setBidState("modes", updatedModes);
  };

  handleCloseErrorModal = () =>
    this.setState({
      sendButtonClicked: false,
      priceButtonClicked: false,
      bidCreationError: false
    });

  validateShipper = () => {
    const { validationData, dispatch } = this.props;
    const validation = handleShipperValidation(
      this.state.bid,
      cloneDeep(validationData)
    );

    dispatch(updateValidation(validation));
  };

  validateStrategicQuantile = (minValue, maxValue) => {
    const { validationData, dispatch } = this.props;
    const validation = handleStrategicQuantileValidation(
      this.state.bid,
      cloneDeep(validationData),
      minValue,
      maxValue
    );

    dispatch(updateValidation(validation));
  };

  handleConfirmCancellationExit = () => {
    this.setState({ modalOpen: false });
    this.props.history.push("/");
  };

  render() {
    const { step, validationData, user, featureFlags } = this.props;
    const when =
      step > 1 && !this.state.modalOpen && this.state.showConfirmNavigation;
    const {
      bidCreationError,
      sendButtonClicked,
      priceButtonClicked,
      bid: { modes }
    } = this.state;
    const disablePriceButton = modes.some(
      x => x.mode === mode.ltl || x.mode === mode.imdl
    );

    return (
      <div>
        <NavigationBlocker
          when={when}
          onExit={() => {
            this.setState(defaultState());
            this.props.dispatch(resetState());
          }}
        />
        <ConfirmCancellationModal
          open={this.state.modalOpen}
          closeModal={() => {
            this.setState({ modalOpen: false });
          }}
          handleExit={this.handleConfirmCancellationExit}
        />
        <Modal
          title={messages.systemError.defaultMessage}
          open={this.state.bidCreationError}
          handleClose={this.handleCloseErrorModal}
          titleClass="modal__title_light uppercase"
          closeButtonClass="modal__close-button mr-16 mt-16"
          bodyClass="border-top-grey pl-24 pr-24 pt-16 pb-32"
        >
          <p className="mb-24 font-size-16">
            {messages.newBidSystemErrorMessage.defaultMessage}
          </p>
          <div className="mt-8 mb-16">
            &nbsp;
            <button
              className="ml-16 secondary-button pr-20 pl-20 fr univers-67-bold-cond"
              onClick={this.handleCloseErrorModal}
            >
              {messages.returnToForm.defaultMessage}
            </button>
            <button
              onClick={() => this.createBid(this.state.priceButtonClicked)}
              className="primary-button fr univers-67-bold-cond"
            >
              {messages.tryAgain.defaultMessage}
            </button>
          </div>
        </Modal>
        <div className="py-16 page-width">
          <h1 data-testid="new-bid" className="alpha-heading">
            {messages.newBid.defaultMessage}
          </h1>
        </div>
        <div className="pb-32 page-width" style={{ width: "760px" }}>
          <ProgressBar currentStep={step} />
        </div>
        <div className="new-bid-sections pt-48 page-width">
          {step === 1 && (
            <BidInformation
              bid={this.state.bid}
              setBidState={this.setBidState}
              removeError={this.removeError}
              validationData={validationData.bidInformation}
              handleModeSelection={this.handleModeSelection}
              setModeDetails={this.setModeDetails}
              validateShipper={this.validateShipper}
              userDivision={user.user.division}
              disableFTLBid={featureFlags?.disableFTLBid}
              disableIMDLBid={featureFlags?.disableIMDLBid}
            />
          )}
          {step === 2 && (
            <ModeInformation
              bid={this.state.bid}
              removeError={this.removeError}
              validationData={validationData.modeInformation}
              handleModeSelection={this.handleModeSelection}
              setModeDetails={this.setModeDetails}
              userDivision={user.user.division}
              disableIMDLBid={featureFlags?.disableIMDLBid}
            />
          )}
          {step === 3 && (
            <BidStrategy
              bid={this.state.bid}
              setModeDetails={this.setModeDetails}
              removeError={this.removeError}
              validationData={validationData.bidStrategy}
              userDivision={user.user.division}
              validateStrategicQuantile={this.validateStrategicQuantile}
            />
          )}
          {step === 4 && (
            <ReviewBid
              bid={this.state.bid}
              setBidState={this.setBidState}
              setModeDetails={this.setModeDetails}
              removeError={this.removeError}
              validationData={validationData}
              validateShipper={this.validateShipper}
              userDivision={user.user.division}
              validateStrategicQuantile={this.validateStrategicQuantile}
            />
          )}
        </div>
        <div className="mt-28 mb-48 page-width">
          <button
            disabled={step === 1}
            onClick={this.handleBackButton}
            className="secondary-button"
            data-testid="back-button"
          >
            {messages.back.defaultMessage}
          </button>
          <span className="fr">
            <span className="pr-16">
              <button
                onClick={() => {
                  this.setState({ modalOpen: true });
                }}
                className="text-button"
                data-testid="exit-button"
              >
                {messages.exit.defaultMessage}
              </button>
            </span>
            {step !== 4 ? (
              <button
                onClick={this.handleNextButton}
                className="primary-button"
                data-testid="next-button"
                disabled={step === 2 && !modes.length}
              >
                {messages.next.defaultMessage}
              </button>
            ) : (
              <>
                {(() => {
                  const isRxoBid = this.state.bid.bidScac.value !== bidScacType[0].value;
                  const shouldHideSendToPricing = 
                    user.user.role === userRole.salesRep &&
                    isRxoBid &&
                    featureFlags.hideSendToPricingForSales;

                  return (
                    <>
                      {!shouldHideSendToPricing && (
                        <button
                          onClick={() =>
                            !sendButtonClicked &&
                            !priceButtonClicked &&
                            this.createBid(false)
                          }
                          className="secondary-button"
                          data-testid="send-button"
                          style={{ minWidth: "155px" }}
                        >
                          {sendButtonClicked && !bidCreationError ? (
                            <Spinner
                              className="spinner"
                              fill="#69C764"
                              height="16px"
                              width="16px"
                            />
                          ) : (
                            messages.sendToPricing.defaultMessage
                          )}
                        </button>
                      )}
                      {user.user.role === userRole.salesRep && (
                        <span className={shouldHideSendToPricing ? "" : "pl-8"}>
                          <button
                            onClick={() =>
                              !sendButtonClicked &&
                              !priceButtonClicked &&
                              this.createBid(true)
                            }
                            className="primary-button"
                            data-testid="price-button"
                            disabled={disablePriceButton}
                            style={{ minWidth: "130px" }}
                          >
                            {priceButtonClicked && !bidCreationError ? (
                              <Spinner
                                className="spinner"
                                fill="#ffffff"
                                height="16px"
                                width="16px"
                              />
                            ) : (
                              messages.priceMyself.defaultMessage
                            )}
                          </button>
                        </span>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </span>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    newBid: { step, validationData, unsavedChanges },
    user,
    featureFlags
  } = state;
  return {
    step,
    validationData,
    unsavedChanges,
    user,
    featureFlags
  };
};

export default connect(mapStateToProps)(NewBid);
